import {sum, values, keys, includes} from 'lodash'
import * as z from 'zod/mini'
import * as abtests from '../constants/abtests'
import type {AbTest} from '../constants/abtests'
import {hasKey, typedEntries} from './utils'

export const COOKIE_NAME = 'abtests'
export const COOKIE_SEPARATOR = ','
export const VARIANT_SEPARATOR = ':'

const envSchema = z.pipe(
  z.pipe(
    z.string(),
    z.transform((val) => val.split(';')),
  ),
  z.array(z.enum(abtests.ALL_AB_TESTS)),
)

export const getEnabledTests = () => {
  // eslint-disable-next-line no-process-env
  const env = process.env.GATSBY_ABTESTS
  if (!env) return []
  const res = envSchema.safeParse(env)
  if (!res.success) return []
  return res.data
}

export type AbtestCurrent = {[K in AbTest]?: Extract<(typeof conf)[K]['variants'][number], string>}

const pickRandom = <T extends string>(variants: Record<T, number>) => {
  const total = sum(values(variants))
  const random = Math.random() * total
  let max = 0
  for (const variant of keys(variants) as T[]) {
    // Generic Record so always true
    max += variants[variant]
    if (random <= max) return variant
  }
  return null // Invariant, but fail gracefully
}

type Test<T extends string, U extends T> = {
  priority: number
  variants: T[]
  pickVariant: (current?: AbtestCurrent) => U | null
}

const testFactory = <T extends string, U extends T>({variants, priority, pickVariant}: Test<T, U>): Test<T, U> => {
  return {
    priority,
    pickVariant,
    variants,
  }
}

export const conf = {
  [abtests.SCOOTER]: testFactory({
    priority: 1,
    variants: ['true'],
    pickVariant: (picked) => {
      if (picked?.[abtests.SCOOTER]) return null
      return pickRandom({true: 100} as const)
    },
  }),
  [abtests.ACCIDENT_INSURANCE]: testFactory({
    priority: 1,
    variants: ['off', 'show', 'hide'],
    pickVariant: (picked) => {
      if (picked?.[abtests.ACCIDENT_INSURANCE]) return null
      return pickRandom({off: 50, show: 25, hide: 25} as const)
    },
  }),
} as const

export const setVariant = <T extends AbTest>(
  result: AbtestCurrent,
  test: T,
  variant: NonNullable<AbtestCurrent[T]>,
) => {
  result[test] = variant
}

const isValidTest = (test: string): test is AbTest => hasKey(conf, test)

const isValidVariant = <T extends AbTest>(test: T, variant: string): variant is NonNullable<AbtestCurrent[T]> => {
  const {variants} = conf[test]
  return includes(variants, variant)
}

export const parseAbtests = (values: string[]): AbtestCurrent => {
  const result: AbtestCurrent = {}
  for (const curr of values) {
    const [test, variant] = curr.split(VARIANT_SEPARATOR)

    if (test && variant && isValidTest(test) && isValidVariant(test, variant)) {
      setVariant(result, test, variant)
    }
  }
  return result
}

export const formatAbtest = <T extends AbTest>(test: T, variant: (typeof conf)[T]['variants'][number]) =>
  `${test}${VARIANT_SEPARATOR}${variant}`
export const formatAbtests = (values: AbtestCurrent) => {
  return typedEntries(values).map(([key, value]) => formatAbtest(key, value))
}

export const isVariantActive = <T extends AbTest>(
  abtests: AbtestCurrent | undefined,
  test: T,
  variant: (typeof conf)[T]['variants'][number],
) => {
  if (!abtests) return false
  return abtests[test] === variant
}
